import { type ReactNode, useEffect } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  headerActions?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  noScroll?: boolean;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  headerActions,
  size = 'md',
  noScroll = false,
}: ModalProps) {
  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 dark:bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div
        className={`relative bg-white dark:bg-neutral-900 rounded-xl shadow-2xl w-full mx-4 ${sizeClasses[size]} max-h-[90vh] flex flex-col`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border dark:border-neutral-700">
          <h2 className="text-xl font-semibold text-text-dark dark:text-neutral-100">{title}</h2>
          <div className="flex items-center gap-3">
            {headerActions}
            <button
              onClick={onClose}
              className="p-1 rounded-lg text-text-muted dark:text-neutral-400 hover:text-text-dark dark:hover:text-neutral-200 hover:bg-surface-secondary dark:hover:bg-neutral-800 transition-colors"
              aria-label="Close modal"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className={`flex-1 ${noScroll ? 'overflow-hidden' : 'overflow-y-auto'} overflow-x-hidden px-6 py-4`}>{children}</div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-surface-border dark:border-neutral-700">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
